import axios from 'axios';

// Resolve the API base URL safely.
// - If REACT_APP_BACKEND_URL is empty or missing -> use same-origin (relative /api).
// - If the configured URL uses http:// but the page is served over https://,
//   auto-upgrade to https:// to avoid Mixed-Content blocking.
function resolveApiBase() {
  let raw = (process.env.REACT_APP_BACKEND_URL || '').trim();
  if (!raw) return '';
  if (typeof window !== 'undefined' &&
      window.location.protocol === 'https:' &&
      raw.startsWith('http://')) {
    raw = 'https://' + raw.slice('http://'.length);
  }
  return raw.replace(/\/+$/, '');
}

const API_URL = resolveApiBase();

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Helper function to format API error details
export function formatApiErrorDetail(detail) {
  if (detail == null) return 'Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

// Auth API
export const authAPI = {
  register: async (userData) => {
    try {
      const { data } = await api.post('/auth/register', userData);
      return data;
    } catch (error) {
      throw new Error(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    }
  },

  login: async (credentials) => {
    try {
      const { data } = await api.post('/auth/login', credentials);
      return data;
    } catch (error) {
      throw new Error(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    }
  },

  logout: async () => {
    try {
      const { data } = await api.post('/auth/logout');
      return data;
    } catch (error) {
      throw new Error(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    }
  },

  getMe: async () => {
    try {
      const { data } = await api.get('/auth/me');
      return data;
    } catch (error) {
      throw new Error(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    }
  }
};

// Projects API
export const projectsAPI = {
  getAll: async () => {
    try {
      const { data } = await api.get('/projects');
      return data;
    } catch (error) {
      throw new Error(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    }
  },

  getOne: async (id) => {
    try {
      const { data } = await api.get(`/projects/${id}`);
      return data;
    } catch (error) {
      throw new Error(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    }
  },

  create: async (projectData) => {
    try {
      const { data } = await api.post('/projects', projectData);
      return data;
    } catch (error) {
      throw new Error(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    }
  },

  update: async (id, projectData) => {
    try {
      const { data } = await api.put(`/projects/${id}`, projectData);
      return data;
    } catch (error) {
      throw new Error(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    }
  },

  delete: async (id) => {
    try {
      const { data } = await api.delete(`/projects/${id}`);
      return data;
    } catch (error) {
      throw new Error(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    }
  }
};

// Contact API
export const contactAPI = {
  submit: async (contactData) => {
    try {
      const { data } = await api.post('/contact', contactData);
      return data;
    } catch (error) {
      throw new Error(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    }
  },

  getAll: async () => {
    try {
      const { data } = await api.get('/contact');
      return data;
    } catch (error) {
      throw new Error(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    }
  }
};

// Admin API
export const adminAPI = {
  getUsers: async () => {
    try {
      const { data } = await api.get('/admin/users');
      return data;
    } catch (error) {
      throw new Error(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    }
  },

  assignMediator: async (userId, mediatorId) => {
    try {
      const { data } = await api.put(`/admin/users/${userId}/mediator`, { mediatorId });
      return data;
    } catch (error) {
      throw new Error(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    }
  },

  deleteUser: async (userId) => {
    try {
      const { data } = await api.delete(`/admin/users/${userId}`);
      return data;
    } catch (error) {
      throw new Error(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    }
  },

  getStats: async () => {
    try {
      const { data } = await api.get('/admin/stats');
      return data;
    } catch (error) {
      throw new Error(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    }
  },

  getAnalytics: async () => {
    try {
      const { data } = await api.get('/admin/analytics');
      return data;
    } catch (error) {
      throw new Error(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    }
  }
};

// Team / Staff API (admin manages sub-admin & editor accounts)
export const teamAPI = {
  list: async () => {
    try {
      const { data } = await api.get('/admin/team');
      return data;
    } catch (error) {
      throw new Error(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    }
  },
  create: async (payload) => {
    try {
      const { data } = await api.post('/admin/team', payload);
      return data;
    } catch (error) {
      throw new Error(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    }
  },
  remove: async (id) => {
    try {
      const { data } = await api.delete(`/admin/team/${id}`);
      return data;
    } catch (error) {
      throw new Error(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    }
  }
};

// Mediator / Vermittler API
export const mediatorsAPI = {
  // Admin views
  adminList: async () => {
    try {
      const { data } = await api.get('/mediators/admin');
      return data;
    } catch (error) {
      throw new Error(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    }
  },
  adminCreate: async (payload) => {
    try {
      const { data } = await api.post('/mediators/admin', payload);
      return data;
    } catch (error) {
      throw new Error(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    }
  },
  adminRemove: async (id) => {
    try {
      const { data } = await api.delete(`/mediators/admin/${id}`);
      return data;
    } catch (error) {
      throw new Error(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    }
  },
  // Mediator self-service
  myOverview: async () => {
    try {
      const { data } = await api.get('/mediators/me/overview');
      return data;
    } catch (error) {
      throw new Error(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    }
  },
  myInvestors: async () => {
    try {
      const { data } = await api.get('/mediators/me/investors');
      return data;
    } catch (error) {
      throw new Error(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    }
  },
  myNetwork: async () => {
    try {
      const { data } = await api.get('/mediators/me/network');
      return data;
    } catch (error) {
      throw new Error(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    }
  },
  createSub: async (payload) => {
    try {
      const { data } = await api.post('/mediators/me/sub-mediators', payload);
      return data;
    } catch (error) {
      throw new Error(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    }
  },
  removeSub: async (id) => {
    try {
      const { data } = await api.delete(`/mediators/me/sub-mediators/${id}`);
      return data;
    } catch (error) {
      throw new Error(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    }
  },
  // Public referral verification
  verifyCode: async (code) => {
    try {
      const { data } = await api.get(`/mediators/verify/${code}`);
      return data;
    } catch (error) {
      throw new Error(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    }
  }
};

// Downloads API
export const downloadsAPI = {
  // Public list
  getPublic: async (category = null) => {
    try {
      const params = { publicOnly: true };
      if (category) params.category = category;
      const { data } = await api.get('/downloads/', { params });
      return data;
    } catch (error) {
      throw new Error(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    }
  },

  // Admin list (includes private)
  getAllAdmin: async () => {
    try {
      const { data } = await api.get('/downloads/admin');
      return data;
    } catch (error) {
      throw new Error(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    }
  },

  upload: async ({ file, title, description = '', category = 'factsheet', projectId = null, isPublic = true }, onUploadProgress) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (title) formData.append('title', title);
      formData.append('description', description);
      formData.append('category', category);
      if (projectId) formData.append('projectId', projectId);
      formData.append('isPublic', String(isPublic));

      const { data } = await api.post('/downloads/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress
      });
      return data;
    } catch (error) {
      throw new Error(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    }
  },

  update: async (id, payload) => {
    try {
      const { data } = await api.put(`/downloads/${id}`, payload);
      return data;
    } catch (error) {
      throw new Error(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    }
  },

  remove: async (id) => {
    try {
      const { data } = await api.delete(`/downloads/${id}`);
      return data;
    } catch (error) {
      throw new Error(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    }
  },

  // Returns the public download URL (browser will trigger file download)
  getDownloadUrl: (id) => `${API_URL}/api/downloads/${id}/download`
};

export default api;